// eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useState, useEffect, useRef } from "react";
import Layout from "../../components/Layout";
import { ChevronLeft, FilePlus2, X, Upload, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { PostsService, CreatePostRequest } from "../../services/postsService";
import { CategoriesService } from "../../services/categoriesService";
import "./../../styles/member/createarticle.css";
import { showToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import defaultThumbnail from "../../assets/images/default-thumbnail.webp";
import CKEditorWrapper from "../../components/CKEditorWrapper";
import { toAbsoluteUrl } from "../../config/api";

const CreateArticle: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    metaDescription: "",
    primaryKeyword: "",
    secondaryKeywords: "",
    anchorText: "",
    featuredImage: "",
    category: "",
    categoryId: undefined as number | undefined,
    tags: [] as string[],
    internalLinks: [] as string[],
    externalLinks: [] as string[],
  });
  // const DEFAULT_IMAGE = "/images/default-thumbnail.webp";
  const DEFAULT_IMAGE = defaultThumbnail;
  const [previewImage, setPreviewImage] = useState<string>(DEFAULT_IMAGE);

  // Constants for validation
  const TITLE_MAX_LENGTH = 100;
  const META_DESCRIPTION_MAX_LENGTH = 160;
  const CONTENT_MIN_LENGTH = 100;
  const [moderationWarning, setModerationWarning] = useState<{
    show: boolean;
    message: string;
    flaggedTerms: string[];
  }>({ show: false, message: "", flaggedTerms: [] });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tải danh mục khi component mount
  useEffect(() => {
    // 1️⃣ Tải danh mục khi component mount
    const loadCategories = async () => {
      try {
        const response = await CategoriesService.listCategories({ limit: 100 });
        setCategories(response.data);
      } catch (err) {
        showToast.error("Không thể tải danh mục. Vui lòng thử lại sau.");
      }
    };
    loadCategories();

    // 2️⃣ Cập nhật ảnh xem trước
    const timeout = setTimeout(() => {
      if (!formData.featuredImage) {
        setPreviewImage(DEFAULT_IMAGE);
      } else {
        // Sử dụng toAbsoluteUrl để đảm bảo URL đúng
        const imageUrl = formData.featuredImage.startsWith('http') 
          ? formData.featuredImage 
          : toAbsoluteUrl(formData.featuredImage);
        setPreviewImage(imageUrl);
      }
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.featuredImage]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast.error('Chỉ chấp nhận file ảnh (JPG, JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Kích thước file không được vượt quá 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await PostsService.uploadImage(file);
      handleInputChange("featuredImage", result.url);
      showToast.success(result.message || 'Upload hình ảnh thành công');
    } catch (error: any) {
      showToast.error(error.message || 'Upload hình ảnh thất bại');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showToast.error("Tiêu đề bài viết không được để trống");
      return false;
    }
    if (formData.title.length > TITLE_MAX_LENGTH) {
      showToast.error(
        `Tiêu đề bài viết không được vượt quá ${TITLE_MAX_LENGTH} ký tự`
      );
      return false;
    }
    if (!formData.content.trim()) {
      showToast.error("Nội dung bài viết không được để trống");
      return false;
    }
    if (formData.content.length < CONTENT_MIN_LENGTH) {
      showToast.error(
        `Nội dung bài viết phải có ít nhất ${CONTENT_MIN_LENGTH} ký tự`
      );
      return false;
    }
    if (!formData.category && !formData.categoryId) {
      showToast.error("Vui lòng chọn danh mục");
      return false;
    }
    // if (!formData.featuredImage.trim()) {
    //   showToast.error("URL hình ảnh nổi bật không được để trống");
    //   return false;
    // }
    // if (
    //   !/^https?:\/\/[^\s/$.?#].[^\s]*\.(?:png|jpg|jpeg|webp|gif)$/i.test(
    //     formData.featuredImage
    //   )
    // ) {
    //   showToast.error(
    //     "URL hình ảnh nổi bật không hợp lệ. Vui lòng nhập URL hình ảnh hợp lệ (png, jpg, jpeg, webp, gif)."
    //   );
    //   return false;
    // }
    if (formData.metaDescription.length > META_DESCRIPTION_MAX_LENGTH) {
      showToast.error(
        `Mô tả Meta không được vượt quá ${META_DESCRIPTION_MAX_LENGTH} ký tự`
      );
      return false;
    }
    if (
      formData.externalLinks[0] &&
      !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(formData.externalLinks[0])
    ) {
      showToast.error(
        "URL backlink không hợp lệ. Vui lòng nhập URL hợp lệ (bắt đầu bằng http:// hoặc https://)."
      );
      return false;
    }
    return true;
  };

  const showSuccessToast = (isOverride: boolean = false) => {
    showToast.success(
      isOverride
        ? "Bài viết đã được tạo thành công (bỏ qua cảnh báo)!"
        : "Bài viết đã được tạo thành công!"
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Chuyển đổi chuỗi từ khóa phụ thành mảng
      const secondaryKeywords = formData.secondaryKeywords
        ? formData.secondaryKeywords
            .split(",")
            .map((k: string) => k.trim())
            .filter((k: string) => k)
        : [];

      // Xử lý externalLinks - validate URL hợp lệ
      const externalLinks =
        formData.externalLinks
          .filter((link) => link.trim())
          .filter((link) => /^https?:\/\//.test(link.trim())) || [];

      const submitData: CreatePostRequest = {
        ...formData,
        secondaryKeywords,
        externalLinks,
        category: formData.category || undefined,
        categoryId: formData.categoryId || undefined,
      };

      // Kiểm duyệt trước khi submit
      const imageUrls: string[] = [];
      try {
        const urlRegex = /(https?:\/\/[^\s"']+\.(?:png|jpg|jpeg|webp|gif))/gi;
        let m: RegExpExecArray | null;
        while ((m = urlRegex.exec(submitData.content || "")) !== null) {
          imageUrls.push(m[1]);
        }
        if (submitData.featuredImage) imageUrls.push(submitData.featuredImage);
      } catch {}

      const moderation = await PostsService.moderateContent({
        title: submitData.title,
        content: submitData.content,
        imageUrls,
      });
      if (!moderation.isSafe) {
        if (moderation.severity === "warn") {
          setModerationWarning({
            show: true,
            message: moderation.message || "Nội dung có từ ngữ nhạy cảm.",
            flaggedTerms: moderation.flaggedTerms,
          });
          setLoading(false);
          return;
        }
        if (moderation.severity === "block") {
          showToast.error(
            moderation.message || "Nội dung không đạt kiểm duyệt"
          );
          setLoading(false);
          return;
        }
      }

      await PostsService.createPost(submitData);
      showSuccessToast();
      setTimeout(() => navigate("/member/article"), 500);
    } catch (err: any) {
      console.error("Error creating post:", err);
      const errorMessage =
        err.response?.data?.message ||
        (err instanceof Error ? err.message : "Có lỗi xảy ra khi tạo bài viết");
      showToast.error(
        errorMessage.includes("Invalid Service")
          ? "Lỗi dịch vụ backend. Vui lòng thử lại sau hoặc liên hệ hỗ trợ."
          : errorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOverride = async () => {
    try {
      setLoading(true);
      setError(null);
      const secondaryKeywords = formData.secondaryKeywords
        ? formData.secondaryKeywords
            .split(",")
            .map((k: string) => k.trim())
            .filter((k: string) => k)
        : [];
      const externalLinks =
        formData.externalLinks
          .filter((link) => link.trim())
          .filter((link) => /^https?:\/\//.test(link.trim())) || [];
      const submitData: CreatePostRequest = {
        ...formData,
        secondaryKeywords,
        externalLinks,
        category: formData.category || undefined,
        categoryId: formData.categoryId || undefined,
        confirmOverride: true,
      };

      await PostsService.createPost(submitData);
      showSuccessToast(true); // Show success toast for override
      showToast.warning(
        "Tài khoản có thể bị khóa theo chính sách do bỏ qua cảnh báo kiểm duyệt."
      );
      // Delay navigation to ensure toast is visible
      setTimeout(() => navigate("/member/article"), 500);
    } catch (err: any) {
      console.error("Error creating post (override):", err);
      const errorMessage =
        err.response?.data?.message ||
        (err instanceof Error ? err.message : "Có lỗi xảy ra khi tạo bài viết");
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
      setModerationWarning({ show: false, message: "", flaggedTerms: [] });
    }
  };

  return (
    <Layout>
      <div className="article-page">
        {/* Header */}
        <Link to="/member/article" className="btn-back">
          <ChevronLeft size={16} /> Quay lại
        </Link>
        <div className="article-header-infor">
          <div>
            <h2>Tạo bài viết</h2>
            <p>Viết và tối ưu hóa bài viết để đăng tải</p>
          </div>
        </div>

        {/* Thông báo lỗi */}
        {error && (
          <div
            className="error-message"
            style={{
              background: "#fee",
              color: "#c33",
              padding: "10px",
              borderRadius: "4px",
              margin: "10px 0",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-article">
          {moderationWarning.show && (
            <div
              className="modal-overlay"
              onClick={() =>
                setModerationWarning({
                  show: false,
                  message: "",
                  flaggedTerms: [],
                })
              }
            >
              <div
                className="modal-content-cencor"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header-cencor">
                  <h3>Cảnh báo kiểm duyệt</h3>
                  <button
                    onClick={() =>
                      setModerationWarning({
                        show: false,
                        message: "",
                        flaggedTerms: [],
                      })
                    }
                  >
                    <X size={16} />
                  </button>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ color: "#874d00", marginBottom: 4 }}>
                    {moderationWarning.message}
                  </div>
                  {moderationWarning.flaggedTerms?.length > 0 && (
                    <div className="moderation-warning-container">
                      <div className="moderation-warning-title">
                        Từ ngữ vi phạm:
                      </div>
                      <div className="moderation-terms-container">
                        {moderationWarning.flaggedTerms
                          .sort((a: string, b: string) => {
                            const aIsWarn = !a.startsWith("image:");
                            const bIsWarn = !b.startsWith("image:");
                            if (aIsWarn && !bIsWarn) return -1;
                            if (!aIsWarn && bIsWarn) return 1;
                            return 0;
                          })
                          .map((term: string, index: number) => (
                            <span
                              key={index}
                              className={`moderation-term-badge ${
                                !term.startsWith("image:")
                                  ? "moderation-term-badge--warn"
                                  : "moderation-term-badge--block"
                              }`}
                            >
                              {term}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() =>
                        setModerationWarning({
                          show: false,
                          message: "",
                          flaggedTerms: [],
                        })
                      }
                    >
                      Tôi sẽ chỉnh sửa
                    </button>
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={handleConfirmOverride}
                    >
                      Vẫn đăng (có thể bị khóa)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* LEFT */}
          <div className="left-panel">
            {/* Nội dung bài viết */}
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Nội dung bài viết</h3>
                <span className="article-content_subtitle">
                  Thông tin chính của bài viết
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  Tiêu đề bài viết *
                </label>
                <input
                  className={`article-content_input ${
                    formData.title.length > TITLE_MAX_LENGTH
                      ? "input-error"
                      : ""
                  }`}
                  type="text"
                  placeholder="Nhập tiêu đề hấp dẫn cho bài viết"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  maxLength={TITLE_MAX_LENGTH}
                  required
                />
                <span
                  className={`article-content_hint ${
                    formData.title.length > TITLE_MAX_LENGTH ? "hint-error" : ""
                  }`}
                >
                  {formData.title.length}/{TITLE_MAX_LENGTH} ký tự
                  {formData.title.length > TITLE_MAX_LENGTH && (
                    <span style={{ color: "#dc3545", marginLeft: "8px" }}>
                      (Vượt quá giới hạn)
                    </span>
                  )}
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  Nội dung bài viết *
                </label>
                <CKEditorWrapper
                  value={formData.content}
                  onChange={(content) => handleInputChange("content", content)}
                  placeholder="Viết nội dung bài viết chất lượng, ít nhất 100 ký tự......"
                  minLength={CONTENT_MIN_LENGTH}
                />
              </div>
            </div>

            {/* Tối ưu SEO */}
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Tối ưu SEO</h3>
                <span className="article-content_subtitle">
                  Thông tin SEO để bài viết lên top Google
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">Mô tả Meta</label>
                <textarea
                  className="article-content_textarea"
                  placeholder="Mô tả SEO tối đa 160 ký tự..."
                  rows={3}
                  value={formData.metaDescription}
                  onChange={(e) =>
                    handleInputChange("metaDescription", e.target.value)
                  }
                />
                <span className="article-content_hint">
                  {formData.metaDescription?.length || 0}/
                  {META_DESCRIPTION_MAX_LENGTH} ký tự
                </span>
              </div>
              <div className="article-content_seo-grid">
                <div className="article-content_input-group">
                  <label className="article-content_label">Từ khóa chính</label>
                  <input
                    className="article-content_input"
                    type="text"
                    placeholder="Từ khóa chính..."
                    value={formData.primaryKeyword}
                    onChange={(e) =>
                      handleInputChange("primaryKeyword", e.target.value)
                    }
                  />
                </div>
                <div className="article-content_input-group">
                  <label className="article-content_label">Từ khóa phụ</label>
                  <input
                    className="article-content_input"
                    type="text"
                    placeholder="Từ khóa phụ, cách nhau bằng dấu phẩy..."
                    value={formData.secondaryKeywords}
                    onChange={(e) =>
                      handleInputChange("secondaryKeywords", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  URL hình ảnh nổi bật
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    className="article-content_input"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={formData.featuredImage}
                    onChange={(e) =>
                      handleInputChange("featuredImage", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={uploadingImage}
                    className={`upload-btn ${uploadingImage ? "disabled" : ""}`}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Đang tải...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Upload
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Preview Image */}
                <div className="image-preview-container">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="image-preview"
                    onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE)}
                  />
                </div>
              </div>
              {/* <div className="article-content_input-group">
                <label className="article-content_label">Anchor text</label>
                <input
                  className="article-content_input"
                  type="text"
                  placeholder="Văn bản hiển thị cho liên kết"
                  value={formData.anchorText}
                  onChange={(e) =>
                    handleInputChange("anchorText", e.target.value)
                  }
                />
              </div> */}
            </div>
          </div>

          {/* RIGHT */}
          <div className="right-panel">
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Thông tin bài viết</h3>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">Danh mục *</label>
                <select
                  className="article-content_select"
                  value={formData.categoryId || ""}
                  onChange={(e) => {
                    const categoryId = e.target.value
                      ? parseInt(e.target.value)
                      : undefined;
                    const category = categories.find(
                      (c) => c.id === categoryId
                    );
                    handleInputChange("categoryId", categoryId);
                    handleInputChange("category", category?.name || "");
                  }}
                  required
                >
                  <option value="">Chọn danh mục</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Thông tin Backlink */}
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Thông tin Backlink</h3>
                <span className="article-content_subtitle">
                  Thông tin về liên kết bạn muốn xây dựng
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">URL Backlink</label>
                <input
                  className="article-content_input"
                  type="url"
                  placeholder="https://example.com"
                  value={formData.externalLinks?.[0] || ""}
                  onChange={(e) =>
                    handleInputChange("externalLinks", [e.target.value])
                  }
                />
              </div>
              {/* <div className="article-content_input-group">
                <label className="article-content_label">Anchor text</label>
                <input
                  className="article-content_input"
                  type="text"
                  placeholder="Văn bản hiển thị cho liên kết"
                  value={formData.anchorText}
                  onChange={(e) =>
                    handleInputChange("anchorText", e.target.value)
                  }
                />
              </div> */}
            </div>

            <button
              type="submit"
              className="create-acticle-btn"
              disabled={loading}
            >
              <FilePlus2 size={16} /> {loading ? "Đang tạo..." : "Tạo bài viết"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateArticle;
